import React, { forwardRef, useState, useCallback, useMemo } from 'react';
import { useField } from 'formik';


interface IInputField {
  label?: string;
  value?: string;
  placeholder?: string;
  name: string;
  labelJustify?: 'start' | 'space-between';
  labelColor?: 'default' | 'light';
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'date';
  optional?: boolean;
  disabled?: boolean;
  autocomplete?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  link?: {
    text: string;
    href: string;
  };
  help?: string;
  error?: string;
  touched?: boolean;
  readonly?: boolean;
  required?: boolean;
  className?: string;
  extraMessage?: React.ReactNode;
}

// Base Input Field Component (can be used standalone)
const BaseInputField = forwardRef<HTMLInputElement, IInputField>(
  (
    {
      label,
      value = '',
      placeholder,
      name,
      labelJustify = 'start',
      labelColor = 'default',
      type = 'text',
      optional = false,
      disabled = false,
      autocomplete = true,
      onChange,
      link,
      help = '',
      touched = false,
      error = '',
      readonly = false,
      required = false,
      className = '',
      extraMessage,
      onBlur,
      onFocus,
    },
    ref
  ) => {
    const [fieldType, setFieldType] = useState(type);

    const autocompleteMemo = useMemo(() => {
      if (!autocomplete) {
        return type === 'password' ? 'new-password' : 'off';
      }
      return '';
    }, [type, autocomplete]);

    const changeTypeHandler = useCallback(() => {
      setFieldType((state) => (state === 'password' ? 'text' : 'password'));
    }, []);

    const disableOnWheel = (e: React.WheelEvent<HTMLInputElement>) =>
      (e.target as HTMLInputElement).blur();

    // Label classes
    const labelClasses = [
      'font-medium text-base mb-1',
      !disabled && labelColor === 'light' ? 'text-white' : '',
      !disabled && labelColor !== 'light' ? 'text-black' : '',
    ]
      .filter(Boolean)
      .join(' ');

    // Label box classes
    const labelBoxClasses = [
      'flex items-center gap-1',
      labelJustify === 'space-between' ? 'justify-between' : '',
    ]
      .filter(Boolean)
      .join(' ');

    // Input classes
    const inputClasses = [
      'form-field h-12 w-full px-3 py-2 bg-white text-base text-gray-850',
      'border border-gray-200 rounded',
      'focus:border-blue-300 focus:ring-1 focus:ring-blue-300 focus:outline-none',
      'hover:border-blue-300',
      'placeholder:text-gray-500 placeholder:font-normal',
      'disabled:text-gray-500 disabled:cursor-not-allowed disabled:hover:border-gray-200',
      type === 'password' ? 'pr-14' : '',
      error && touched
        ? 'border-red-500 focus:border-red-500 focus:ring-red-300'
        : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={className}>
        <div>
          {label && (
            <div className={labelBoxClasses}>
              <label
                htmlFor={name}
                className={labelClasses}
              >
                {label}{' '}
                {optional && <span className="text-gray-500">(optional)</span>}
              </label>
              {required && (
                <span className="text-red-500 text-sm font-medium">*</span>
              )}
              {help && (
                <span
                  className="inline-flex items-center justify-center w-4 h-4 text-xs text-gray-400 border border-gray-400 rounded-full cursor-help"
                  title={help}
                >
                  ?
                </span>
              )}

              {link && (
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 flex items-center ml-auto"
                >
                  <span className="font-normal text-xs leading-[18px] mr-1.5">
                    {link.text}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="text-blue-600"
                  >
                    <path
                      d="M6 3H3v10h10v-3"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M9 2h5v5M14 2l-6 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              )}
            </div>
          )}

          <div className="relative input-container">
            <input
              ref={ref}
              id={name}
              name={name}
              value={value}
              className={inputClasses}
              autoComplete={autocompleteMemo}
              type={fieldType}
              placeholder={placeholder}
              onChange={onChange}
              readOnly={readonly}
              disabled={disabled}
              onWheel={disableOnWheel}
              onBlur={onBlur}
              onFocus={onFocus}
              style={{
                WebkitAppearance: type === 'number' ? 'none' : undefined,
                MozAppearance: type === 'number' ? 'textfield' : undefined,
              }}
            />
            {type === 'password' && (
              <button
                type="button"
                className="password-toggle absolute bottom-3.5 right-5 cursor-pointer text-gray-500"
                onClick={changeTypeHandler}
                tabIndex={-1}
              >
                {fieldType === 'password' ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line
                      x1="1"
                      y1="1"
                      x2="23"
                      y2="23"
                    />
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                    />
                  </svg>
                )}
              </button>
            )}
          </div>

          {touched && error && (
            <p
              className="text-red-500 text-sm mt-1"
              id={`${name}-error`}
            >
              {error}
            </p>
          )}

          {extraMessage && extraMessage}
        </div>
      </div>
    );
  }
);

BaseInputField.displayName = 'BaseInputField';

// Formik-compatible Input Field Component
type FormikInputFieldProps = Omit<
  IInputField,
  'value' | 'onChange' | 'error' | 'touched'
>;

const FormikInputField = forwardRef<HTMLInputElement, FormikInputFieldProps>(
  (
    {
      label,
      labelColor = 'default',
      placeholder,
      labelJustify,
      name,
      type = 'text',
      optional = false,
      disabled = false,
      link,
      help = '',
      autocomplete = true,
      readonly = false,
      required = false,
      className,
      extraMessage,
      onBlur,
      onFocus,
    },
    ref
  ) => {
    const [{ value }, { touched, error }, { setValue, setTouched }] =
      useField<string>(name);

    const handleOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value, true);
        setTouched(true, false);
      },
      [setValue, setTouched]
    );

    return (
      <BaseInputField
        ref={ref}
        name={name}
        value={value}
        label={label}
        labelJustify={labelJustify}
        labelColor={labelColor}
        placeholder={placeholder}
        onChange={handleOnChange}
        type={type}
        onBlur={onBlur}
        onFocus={onFocus}
        optional={optional}
        disabled={disabled}
        help={help}
        link={link}
        autocomplete={autocomplete}
        touched={touched}
        error={error}
        readonly={readonly}
        required={required}
        className={className}
        extraMessage={extraMessage}
      />
    );
  }
);

FormikInputField.displayName = 'FormikInputField';

// Export both versions
export { BaseInputField as InputField, FormikInputField };
export default FormikInputField;
